import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import type { FabCorner } from '@/hooks/use-fab-corner';

/**
 * Where the AI chat panel is on screen, so the feedback launcher can step to a
 * corner it is not in (useFabAutoCorner). The docked drawer sits at the inline
 * end, `width` px wide (a wide one reaches the launcher's corner); the
 * maximized window covers the page; the floating window snaps to a physical
 * corner.
 */
export type AiChatPanelPlacement =
  | { mode: 'drawer'; width: number }
  | { mode: 'maximized' }
  | { mode: 'pip'; corner: FabCorner };

interface UIState {
  sidebarCollapsed: boolean;
  toggleSidebar: () => void;
  // Active sidepanel
  activeSidepanel: string | null;
  sidepanelData: Record<string, unknown> | null;
  openSidepanel: (id: string, data?: Record<string, unknown>) => void;
  closeSidepanel: () => void;
  // Modal/dialog open count — drives the auto-relocate FAB so it doesn't
  // sit on top of an active dialog/sheet/drawer. The FabStackHost runs a
  // MutationObserver that auto-fires push/pop on any
  // `[data-state="open"]` portal node, but any consumer can also write
  // directly. Floor at 0 so a stray pop never breaks the count.
  modalOpenCount: number;
  pushModal: () => void;
  popModal: () => void;
  // Published by ChatCompanion — null whenever it draws no panel. Runtime
  // only: never persisted, so a reload starts with the launcher in place.
  aiChatPanel: AiChatPanelPlacement | null;
  setAiChatPanel: (panel: AiChatPanelPlacement | null) => void;
}

export const useUIStore = create<UIState>()(
  devtools(
    persist(
      (set) => ({
        sidebarCollapsed: false,
        toggleSidebar: () => set((s) => ({ sidebarCollapsed: !s.sidebarCollapsed })),
        activeSidepanel: null,
        sidepanelData: null,
        openSidepanel: (id, data) => set({ activeSidepanel: id, sidepanelData: data || null }),
        closeSidepanel: () => set({ activeSidepanel: null, sidepanelData: null }),
        modalOpenCount: 0,
        pushModal: () => set((s) => ({ modalOpenCount: s.modalOpenCount + 1 })),
        popModal: () => set((s) => ({ modalOpenCount: Math.max(0, s.modalOpenCount - 1) })),
        aiChatPanel: null,
        setAiChatPanel: (panel) => set({ aiChatPanel: panel }),
      }),
      {
        name: 'hf-ui',
        partialize: (s) => ({ sidebarCollapsed: s.sidebarCollapsed }),
      },
    ),
    { name: 'UIStore' },
  ),
);
